import React, { useState, useEffect, useMemo } from 'react';
import { useImpersonation } from '../context/ImpersonationContext';
import { getFeatureStatus } from '../config/featureStatus';
import { NAV_SECTIONS, SCHEDULE_ITEM, ALL_NAV_ITEMS, POPPY_NAV_SECTIONS, POPPY_NAV_KEYS } from '../config/navItems';
import { isItemVisible } from '../config/permissions';

// Workspace switcher — a segmented Ops | Poppy toggle in the top-left picks
// which set of nav items shows. Persisted so the choice survives reloads; the
// active view always wins when it unambiguously belongs to one workspace
// (e.g. landing on Master Data flips the sidebar to Poppy).
const WORKSPACE_STORAGE_KEY = 'ops-workspace';
const OPS_NAV_KEYS = ALL_NAV_ITEMS.filter(i => i.workspace !== 'poppy').map(i => i.key);

function storedWorkspace() {
  try { return localStorage.getItem(WORKSPACE_STORAGE_KEY) === 'poppy' ? 'poppy' : 'ops'; } catch { return 'ops'; }
}

// Section header style shared by the Ops and Poppy workspace nav lists.
const SECTION_TITLE_STYLE = {
  fontSize: 11,
  fontWeight: 600,
  color: '#9CA3AF',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  padding: '12px 8px 4px',
};

// Decide what a tab looks like for the current session.
// Returns one of: 'normal' (live or owner-not-impersonating), 'coming-soon',
// 'hidden-revealed' (QA toggle on), or null (don't render).
function resolveItemVisibility(itemKey, isImpersonating, qaShowHidden) {
  if (!isImpersonating) return 'normal';
  const status = getFeatureStatus(itemKey);
  if (status === 'live') return 'normal';
  if (status === 'coming-soon') return 'coming-soon';
  if (status === 'hidden') return qaShowHidden ? 'hidden-revealed' : null;
  return 'normal';
}

export default function Sidebar({ activeView, onNavigate, hasModule, collapsed, onToggleCollapse }) {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [inboxCount, setInboxCount] = useState(0);
  const [workspace, setWorkspace] = useState(storedWorkspace);

  const opsToken = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('staff-session'))?.opsToken; } catch { return null; }
  }, []);

  const { isImpersonating, impersonatedSession, qaShowHidden } = useImpersonation();
  // When impersonating, use the synthetic session built from the target's
  // role + admin overlay + toggles so the preview matches what they see.
  const session = useMemo(() => {
    if (isImpersonating) return impersonatedSession;
    try { return JSON.parse(localStorage.getItem('staff-session')); } catch { return null; }
  }, [isImpersonating, impersonatedSession]);

  useEffect(() => {
    if (!opsToken) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/performance/inbox/count`, {
          headers: { 'Authorization': `Bearer ${opsToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setInboxCount(data.count || 0);
        }
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [opsToken]);

  // Red "uncompleted tasks due today" badge on the Task Calendar item.
  // TaskCalendar dispatches taskcal-badge-refresh after complete/reopen/
  // create/delete so the count drops as you work through the day.
  const [taskCalCount, setTaskCalCount] = useState(0);
  useEffect(() => {
    if (!opsToken) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const fetchTaskCalCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ops/task-calendar/badge-count`, {
          headers: { 'Authorization': `Bearer ${opsToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setTaskCalCount(data.count || 0);
        }
      } catch {}
    };
    fetchTaskCalCount();
    const interval = setInterval(fetchTaskCalCount, 2 * 60 * 1000);
    window.addEventListener('taskcal-badge-refresh', fetchTaskCalCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('taskcal-badge-refresh', fetchTaskCalCount);
    };
  }, [opsToken]);

  // Poppy sections the current session can actually see — same gates as every
  // other nav item (module access + View As feature status). Stubs have no
  // view behind them yet, so they render coming-soon for everyone, owner
  // included (resolveItemVisibility only applies flags during View As).
  const poppySections = POPPY_NAV_SECTIONS.map(section => ({
    title: section.title,
    items: section.items
      .filter(i => isItemVisible(i, session))
      .map(i => {
        const vis = resolveItemVisibility(i.key, isImpersonating, qaShowHidden);
        return { item: i, vis: i.stub && vis === 'normal' ? 'coming-soon' : vis };
      })
      .filter(x => x.vis !== null),
  })).filter(s => s.items.length > 0);
  const visiblePoppyItems = poppySections.flatMap(s => s.items);
  // The switcher only appears when at least one REAL (non-stub) Poppy page
  // is accessible; stubs alone never unlock the workspace.
  const hasPoppy = visiblePoppyItems.some(x => !x.item.stub);
  const effectiveWorkspace = hasPoppy ? workspace : 'ops';

  // The current view wins when it clearly belongs to one workspace — deep
  // navigation (mobile nav, programmatic setStaffView) auto-switches the
  // sidebar. Views not in the nav keep the stored choice.
  useEffect(() => {
    if (POPPY_NAV_KEYS.includes(activeView)) setWorkspace('poppy');
    else if (OPS_NAV_KEYS.includes(activeView)) setWorkspace('ops');
  }, [activeView]);

  useEffect(() => {
    try { localStorage.setItem(WORKSPACE_STORAGE_KEY, workspace); } catch {}
  }, [workspace]);

  const switchWorkspace = (target) => {
    if (target === effectiveWorkspace) return;
    setWorkspace(target);
    // Land on a view inside the new workspace so the derive effect doesn't
    // immediately flip back. Prefer the app-default Tasks on the ops side.
    if (target === 'poppy') {
      if (!POPPY_NAV_KEYS.includes(activeView)) {
        // Only normally-visible real pages are navigation targets — never a
        // stub, and never a view flagged coming-soon during View As (matches
        // NavItem's click-disable in the same state).
        const first = visiblePoppyItems.find(x => x.vis === 'normal' && !x.item.stub)?.item;
        if (first) onNavigate(first.key);
      }
    } else if (POPPY_NAV_KEYS.includes(activeView)) {
      const opsItems = [SCHEDULE_ITEM, ...NAV_SECTIONS.flatMap(s => s.items)]
        .filter(i => i.workspace !== 'poppy')
        .filter(i => isItemVisible(i, session) && resolveItemVisibility(i.key, isImpersonating, qaShowHidden) === 'normal');
      const dest = opsItems.find(i => i.key === 'tasks') || opsItems[0];
      if (dest) onNavigate(dest.key);
    }
  };

  return (
    <nav
      className="scrollbar-thin"
      style={{
        width: collapsed ? 64 : 220,
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        background: '#fff',
        borderRight: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 200ms ease',
        zIndex: 31,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Logo / workspace toggle. With Poppy access the slot literally shows
          both workspaces as a segmented toggle ("Ops" | "Poppy") under a
          small Cutie Pie brand line; without it, the plain brand as before. */}
      <div style={{
        height: 56,
        display: 'flex',
        flexDirection: !collapsed && hasPoppy ? 'column' : 'row',
        alignItems: !collapsed && hasPoppy ? 'stretch' : 'center',
        justifyContent: !collapsed && hasPoppy ? 'center' : 'flex-start',
        padding: collapsed ? '0 18px' : '0 16px',
        borderBottom: '1px solid #E5E7EB',
        flexShrink: 0,
      }}>
        {collapsed ? (
          <span style={{ fontSize: 20, fontWeight: 700, color: '#F07EA0' }}>C</span>
        ) : hasPoppy ? (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#F07EA0', letterSpacing: 0.5, marginBottom: 4, paddingLeft: 2 }}>
              Cutie Pie
            </div>
            <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 2 }}>
              {[{ key: 'ops', label: 'Ops' }, { key: 'poppy', label: 'Poppy' }].map(w => {
                const active = effectiveWorkspace === w.key;
                return (
                  <button
                    key={w.key}
                    onClick={() => switchWorkspace(w.key)}
                    title={active ? undefined : `Switch to ${w.label}`}
                    style={{
                      flex: 1,
                      padding: '3px 0',
                      border: 'none',
                      borderRadius: 6,
                      background: active ? '#fff' : 'transparent',
                      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                      color: active ? '#F07EA0' : '#6B7280',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: active ? 'default' : 'pointer',
                      fontFamily: 'Inter, sans-serif',
                      transition: 'background 100ms ease, color 100ms ease',
                    }}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#F07EA0', letterSpacing: 0.5 }}>
              Cutie Pie
            </span>
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>Ops</span>
          </div>
        )}
      </div>

      {/* Nav items */}
      <div style={{ flex: 1, padding: '8px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Collapsed-rail workspace mini-toggle — the Book auto-collapses the
            sidebar, so the rail needs its own way to switch workspaces
            without expanding first. Same visibility rule as the full toggle. */}
        {collapsed && hasPoppy && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            padding: '2px 0 6px',
            borderBottom: '1px solid #F3F4F6',
            marginBottom: 4,
          }}>
            {[{ key: 'ops', letter: 'O', title: 'Ops workspace' }, { key: 'poppy', letter: 'P', title: 'Poppy workspace' }].map(w => {
              const active = effectiveWorkspace === w.key;
              return (
                <button
                  key={w.key}
                  onClick={() => switchWorkspace(w.key)}
                  title={w.title}
                  style={{
                    width: 24,
                    height: 20,
                    border: 'none',
                    borderRadius: 6,
                    background: active ? '#F07EA0' : 'transparent',
                    color: active ? '#fff' : '#9CA3AF',
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: active ? 'default' : 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    transition: 'background 100ms ease, color 100ms ease',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#F9FAFB'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  {w.letter}
                </button>
              );
            })}
          </div>
        )}
        {effectiveWorkspace === 'poppy' ? (
          /* Poppy workspace — sectioned by product area, "Poppy" prefix
             dropped. Stubs are never navigable (no view exists), so their
             onClick is withheld and the QA click-unlock doesn't apply. */
          poppySections.map(section => (
            <div key={section.title}>
              {!collapsed && <div style={SECTION_TITLE_STYLE}>{section.title}</div>}
              {collapsed && <div style={{ height: 8 }} />}
              {section.items.map(({ item, vis }) => (
                <NavItem
                  key={item.key}
                  item={{ ...item, label: item.shortLabel || item.label }}
                  active={activeView === item.key}
                  collapsed={collapsed}
                  hovered={hoveredItem === item.key}
                  onHover={setHoveredItem}
                  onClick={item.stub ? undefined : () => onNavigate(item.key)}
                  visibility={vis}
                  qaShowHidden={qaShowHidden && !item.stub}
                />
              ))}
            </div>
          ))
        ) : (
        <>
        {/* Schedule (for staff roles) */}
        {(() => {
          if (!isItemVisible(SCHEDULE_ITEM, session)) return null;
          const vis = resolveItemVisibility(SCHEDULE_ITEM.key, isImpersonating, qaShowHidden);
          if (!vis) return null;
          return (
            <>
              <NavItem
                item={SCHEDULE_ITEM}
                active={activeView === SCHEDULE_ITEM.key}
                collapsed={collapsed}
                hovered={hoveredItem === SCHEDULE_ITEM.key}
                onHover={setHoveredItem}
                onClick={() => onNavigate(SCHEDULE_ITEM.key)}
                visibility={vis}
                qaShowHidden={qaShowHidden}
              />
              <div style={{ height: 8 }} />
            </>
          );
        })()}

        {NAV_SECTIONS.map(section => {
          const visibleItems = section.items
            .filter(i => i.workspace !== 'poppy')
            .filter(i => isItemVisible(i, session))
            .map(i => ({ item: i, vis: resolveItemVisibility(i.key, isImpersonating, qaShowHidden) }))
            .filter(x => x.vis !== null);
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.title}>
              {!collapsed && <div style={SECTION_TITLE_STYLE}>{section.title}</div>}
              {collapsed && <div style={{ height: 8 }} />}
              {visibleItems.map(({ item, vis }) => (
                <NavItem
                  key={item.key}
                  item={item}
                  active={activeView === item.key}
                  collapsed={collapsed}
                  hovered={hoveredItem === item.key}
                  onHover={setHoveredItem}
                  onClick={() => onNavigate(item.key)}
                  badge={item.key === 'inbox' ? inboxCount : item.key === 'task_calendar' ? taskCalCount : 0}
                  visibility={vis}
                  qaShowHidden={qaShowHidden}
                />
              ))}
            </div>
          );
        })}
        </>
        )}
      </div>

      {/* Collapse toggle (desktop only) */}
      <div style={{
        padding: '12px 8px',
        borderTop: '1px solid #E5E7EB',
        flexShrink: 0,
      }}>
        <button
          onClick={onToggleCollapse}
          style={{
            width: '100%',
            padding: '8px 0',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 16,
            color: '#9CA3AF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            transition: 'background 100ms ease',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? '▸' : '◂'}
        </button>
      </div>
    </nav>
  );
}

function NavItem({ item, active, collapsed, hovered, onHover, onClick, badge, visibility = 'normal', qaShowHidden = false }) {
  const isComingSoon = visibility === 'coming-soon';
  const isHiddenRevealed = visibility === 'hidden-revealed';
  // Coming-soon is clickable only when QA toggle is on (qaShowHidden === true).
  const clickable = visibility === 'normal' || isHiddenRevealed || (isComingSoon && qaShowHidden);

  const bg = active ? '#FDF2F7' : hovered && clickable ? '#F9FAFB' : 'transparent';
  const baseColor = active ? '#F07EA0' : '#374151';
  const textColor = isComingSoon && !qaShowHidden ? '#9CA3AF' : baseColor;
  const iconColor = active ? '#F07EA0' : undefined;
  const opacity = isComingSoon && !qaShowHidden ? 0.6 : 1;
  const border = isHiddenRevealed
    ? '1px dashed #b5556a'
    : 'none';
  const borderLeft = active ? '3px solid #F07EA0' : '3px solid transparent';

  let titleText;
  if (isComingSoon) titleText = qaShowHidden ? 'Coming soon (QA: clickable)' : 'Coming soon';
  else if (isHiddenRevealed) titleText = 'Hidden from staff (QA only)';
  else if (collapsed) titleText = item.label;

  return (
    <button
      onClick={clickable ? onClick : undefined}
      onMouseEnter={() => onHover(item.key)}
      onMouseLeave={() => onHover(null)}
      disabled={!clickable}
      title={titleText}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        height: 36,
        padding: collapsed ? '0 0' : '0 8px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        border,
        background: bg,
        borderRadius: 8,
        cursor: clickable ? 'pointer' : 'not-allowed',
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        color: textColor,
        opacity,
        fontFamily: 'Inter, sans-serif',
        transition: 'background 100ms ease',
        position: 'relative',
        borderLeft,
      }}
    >
      <span style={{ fontSize: 16, flexShrink: 0, filter: iconColor ? undefined : 'grayscale(0.3)' }}>
        {item.icon}
      </span>
      {!collapsed && <span style={{ flex: '0 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>}
      {!collapsed && isComingSoon && (
        <span style={{
          marginLeft: 'auto',
          background: '#FEF3C7',
          color: '#92400E',
          fontSize: 9,
          fontWeight: 700,
          padding: '1px 6px',
          borderRadius: 9999,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          Soon
        </span>
      )}
      {!collapsed && isHiddenRevealed && (
        <span style={{
          marginLeft: 'auto',
          background: '#fdf0f4',
          color: '#b5556a',
          fontSize: 11,
          fontWeight: 600,
          padding: '1px 6px',
          borderRadius: 9999,
        }} title="Hidden from staff">
          🚧
        </span>
      )}
      {!collapsed && !isComingSoon && !isHiddenRevealed && badge > 0 && (
        <span style={{
          marginLeft: 'auto',
          background: '#EF4444',
          color: '#fff',
          fontSize: 10,
          fontWeight: 700,
          padding: '1px 6px',
          borderRadius: 9999,
          minWidth: 18,
          textAlign: 'center',
          lineHeight: '16px',
        }}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
